import { monthsArray } from "@Components/DatePicker/helper";
import { submitBookForm } from "services/bookFormHelper";
import gtagEventEmitter, { gtagDuplicateEventEmitter } from "helpers/gtagEventEmitter";
import isMiniProgramUserAgent from "@Utils/isMiniProgramUserAgent";
import { isPaymentActive } from "@Pages/Search/components/SearchListItem/helper";
import urlBuilder from "@Utils/urlBuilder";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import uetEventEmitter from "helpers/uetEventEmitter";
import config from "@Config/index";
import axios from "axios";
import getUniAppHeaders from "helpers/getUniAppHeaders";
import { logger } from "helpers/logger";
import {
  fireLeadFormOneSuccessEvents,
  isAvailable,
  onSubmitLeadFormOne,
} from "@Components/LeadFormModal/utils";
import { getRecordedPropertyNameForPostHog, identifyPostHogUserByEmail } from "@Utils/posthog";
import { createLead } from "services/leadsHelper";
import { setLocalStorageData } from "@Components/LeadFormModal/data/leadFormReducer";
import getActionInitiatedData from "@Components/LeadFormV2/utils/getActionInitiatedData";
import { HAPPY_FLOW_TENANCY_CTAS } from "constants/index";
import getSelectedRoomAndTenancy from "@Pages/PropertyPage/components/PropertyCTAs/utils/getSelectedRoomAndTenancy";
import {
  updateBookFormCompleted,
  updateBookFormInitiated,
  updateEnquire,
  updatePropertyData,
  updatePropertyRoom,
  updatePropertyTenancy,
} from "@App/userHistoryReducer";
import getBookFormLink from "@Pages/PropertyPage/components/PropertyCTAs/utils/getBookFormLink";
import getPaymentFormLink from "@Pages/PropertyPage/components/PropertyCTAs/utils/getPaymentFormLink";
import getCookie from "helpers/getCookie";
import initiateBookformAPI from "./data/initiateBookFormAPI";
import updateInitiatePayment from "@Pages/Payment/components/PaymentSummary/helpers/updateInitiatePayment";
import currencyData from "constants/currencyConstants";
import fetchPaymentInfo from "@Pages/Payment/components/PaymentSummary/helpers/fetchPaymentInfo";
import { updatePaymentInfo, updatePriceSummary } from "./data/bookFormReducer";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import getPaymentSummary from "@Utils/getPaymentSummary";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import { getTotalPaymentValue } from "@Pages/Payment/utils";
import buildWeChatPayBookingParams from "@Pages/Payment/utils/buildWeChatPayNavParams";
import getFormattedSourceDetails from "@Utils/getFormattedSourceDetails";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, getAppSourceDetails, IdleTimerStage } from "@Utils/webApputils/analyticEvents";
import { encryptDataString } from "helpers/encryption";
import { cancelIdleTimer } from "@Utils/webviewEvents";
import { isApp } from "hooks/useIsApp";
import { publishWebState } from "@Utils/webApputils/nativeStateBridge";
import { getRoomDetailsEventProps, getSectionNameFromUrl } from "helpers/roomDetailsTracking";

// import { captureMessage, SeverityLevel } from "@sentry/node";

/* eslint-disable import/prefer-default-export */

const findKeyByValue = (obj: any = {}, value: any) =>
  Object.keys(obj).find((key) => obj[key] === value) || null;

const getAccommodationFormDefaultValue = ({ defaultState, roomTypeOptions }: any) => {
  const defaultFormData: any = {};

  // Prefilling room type
  if (defaultState?.config_name) {
    const selectedRoomId = findKeyByValue(roomTypeOptions, defaultState.config_name);
    if (selectedRoomId) defaultFormData["roomType"] = selectedRoomId;
  }

  if (defaultState.lease_duration) defaultFormData["duration"] = defaultState.lease_duration;

  if (defaultState.inventory_id) {
    if (defaultFormData.roomType === defaultState.inventory_id) {
      // Other move in case
      defaultFormData["inventory_id"] = ` ${defaultFormData.roomType}`;
    } else if (defaultState.inventory_id.includes(defaultFormData.roomType)) {
      const tenancyId = defaultState.inventory_id.split(defaultFormData.roomType)[1];
      defaultFormData["inventory_id"] = tenancyId;
    } else {
      defaultFormData["inventory_id"] = defaultState.inventory_id;
    }
  }

  // move in and duration ====> flexible
  // inventory_id === roomType
  if (defaultState.lease_duration === "flexible") {
    defaultFormData["inventory_id"] = "flexible";
  }

  if (defaultState.custom_duration) {
    defaultFormData["custom_duration"] = defaultState.custom_duration;
  }

  if (defaultState.arrival_date) {
    const arrivalDate = new Date(defaultState.arrival_date);
    const date = arrivalDate.getDate();
    const month = arrivalDate.getMonth();
    const year = arrivalDate.getFullYear();
    const moveInDate = `${monthsArray[month]} ${date}, ${year}`;
    defaultFormData["arrival_date"] = moveInDate;
  }

  return defaultFormData;
};

// TODO - @Prathamesh
// export const captureCustomMessage = (label: any, value = {}, level: any = "info") => {
//   captureMessage(label, { level, extra: value });
// };

const formatTextInputDateToString = (dateString: string) => {
  // Normalise first: prefilled partial-form data can reach submit still in API ISO/YYYY-MM-DD
  // form (e.g. the guarantor step auto-submits the saved object before the form effects run).
  // Splitting an ISO string on "-" would serialise garbage like "20T00:00:00+05:30-01-2010".
  const display = toDisplayDob(dateString);
  const normalized = display ?? dateString;
  const ymdMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (config.IS_CHINA_APP && ymdMatch) {
    return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
  }
  const [day, month, year] = normalized.split("-");

  return [year, month, day].join("-");
};

const formatDateToString = (dateVal: any) => {
  const date = new Date(dateVal);
  if (date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return null;
};

export const getQueryParams = () => {
  const queryParams: any = {};
  if (typeof window !== "undefined") {
    const urlObj = new URL(window.location.href);
    for (const [key, value] of urlObj.searchParams.entries()) {
      queryParams[key] = value;
    }
  }
  return queryParams;
};

export const getAvailableStartingPricingHelper = (pricingObject: any) => {
  if (pricingObject?.min_available_price) {
    return pricingObject.min_available_price;
  }
  if (pricingObject?.available_price) {
    return pricingObject.available_price;
  }
  if (pricingObject?.min_price) {
    return pricingObject.min_price;
  }
  return pricingObject?.price;
};

/**
 * Returns the price for the selected tenancy.
 * @param {Object} inventory The inventory object.
 * @param {string} selectedInventoryId The selected inventory id.
 * @returns {string} The price for the selected tenancy.
 */
export const getPriceForSelectedTenancy = (inventory: any, selectedInventoryId: number): string => {
  if (!inventory) return "";
  if (inventory.id === selectedInventoryId)
    return getAvailableStartingPricingHelper(inventory.pricing);
  let finalPrice;
  if (inventory.active_children?.length > 0) {
    for (const item of inventory.active_children) {
      finalPrice = getPriceForSelectedTenancy(item, selectedInventoryId);
      if (finalPrice) break;
    }
  }
  return finalPrice ?? "";
};

const getPhoneWithCode = (code: any, phone: any) => `${code}${phone}`;

// get payment Url for inventory
export const getPaymentFullUrl = (inventory: any, params = {}) => {
  const canonicalName = inventory.canonical_name;
  const path = `/places/${canonicalName}/payment`;
  return urlBuilder(path, { params });
};

const handleEvents = (form_uuid: any, eventsData: any, inventory: any) => {
  identifyPostHogUserByEmail(eventsData?.email, {
    funnel: "book_form",
    form_id: form_uuid,
    phone: eventsData?.phone,
    country_code: eventsData?.country_code,
    // Everything about the booking moves as one unit. Person properties
    // persist, so a lone `booked_property_at` would put a fresh timestamp on a
    // stale `booked_property` from an earlier booking — and a region written
    // without its property could leave a push rendering a property that is not
    // in the region beside it.
    ...(() => {
      const property = inventory?.canonical_name;
      if (!property) return {};
      // Region sits in one of three places depending on how the inventory was
      // loaded — the same fallback chain LeadForm's ActionButton uses.
      const region =
        inventory?.region?.canonical_name ||
        inventory?.config?.region?.canonical_name ||
        inventory?.parent?.region?.canonical_name;
      // The inventory often has no name; reuse the one recorded for this slug.
      const displayName = inventory?.name || getRecordedPropertyNameForPostHog(property);
      return {
        booked_property: property,
        ...(displayName ? { booked_property_name: displayName } : {}),
        booked_property_at: new Date().toISOString(),
        ...(region ? { booked_region: region } : {}),
      };
    })(),
  });

  if (form_uuid) {
    gtagDuplicateEventEmitter({
      action: "success",
      form_id: form_uuid,
      category: "book_form",
      ...eventsData,
      ...getRoomDetailsEventProps(getSectionNameFromUrl()),
    });

    // For mini program fire conversion_book_form
    if (isMiniProgramUserAgent()) {
      gtagEventEmitter({
        action: "conversion_book_form",
        form_id: form_uuid,
        category: "book_form",
        ...eventsData,
      });
    }
  }

  // Bing conversion
  uetEventEmitter({ action: "book_form_submit", category: "book_form" });

  // Facebook conversion (custom event)
  // @ts-ignore
  if (typeof fbq !== "undefined" && fbq !== null) {
    const contentIds = inventory?.id ? [inventory.id.toString()] : undefined;
    // @ts-ignore
    // custom event
    fbq("trackCustom", "conversion_book_form", contentIds ? { content_ids: contentIds } : {}, {
      eventID: form_uuid,
    });
    // @ts-ignore
    // standard event
    fbq("track", "CompleteRegistration", contentIds ? { content_ids: contentIds } : {});
  }
};

const getLeadPaymentLeadSuccessStatus = async (leadUUID: any) => {
  const params = {
    cache: false,
    methods: "payments_success_present",
  };
  try {
    const lead = await axios.get(`${config.API_URL}/api/v0/leads/${leadUUID}`, {
      params,
    });
    return lead?.data?.data?.payments_success_present || false;
  } catch (err) {
    logger.error(err);
    return false;
  }
};

const checkIfTenancyAvailable = (accommodationDetails: any = {}, inventory: any = {}) => {
  if (accommodationDetails?.tenancyId || accommodationDetails?.inventory_id) {
    const { selectedRoom, selectedTenancy }: any = getSelectedRoomAndTenancy({
      rooms: inventory.active_children,
      id: accommodationDetails?.tenancyID || accommodationDetails?.inventory_id,
    });

    return isAvailable(selectedRoom) && isAvailable(selectedTenancy);
  }
  return false;
};

const getPaymentInfoToRedirectPaymentPage = async (
  id: string,
  isActivePayment: boolean,
  inventory_id: number,
  dispatch: any,
  isUserLoggedIn: boolean,
) => {
  const isCookiePresent = getCookie("user_flow_token");
  if (isActivePayment && (isCookiePresent || isUserLoggedIn)) {
    const paymentInfo = await fetchPaymentInfo({ id, inventory_id, source: "bookform util" });

    const { amount = 0 } = getTotalPaymentValue(paymentInfo.payment_info);

    dispatch(updatePaymentInfo({ ...paymentInfo, id: inventory_id }));

    return amount === 0;
  }
  return false;
};
export const getRoomName = (inv: Record<string, any>, roomType: number) => {
  const room = inv.active_children?.find((item: Record<string, any>) => +item.id === +roomType);
  return room?.name || "";
};

const redirectToPaymentPage = async (
  lead_uuid: any,
  lead_id: number | undefined,
  inv: any,
  user_details: any,
  formSubmitRedirectUrl: any,
  queryParams: any,
  nationality: any,
  disableLoading: any,
  history: any,
  isDesktop: boolean,
  dispatch: any,
  inventory_id: number,
  accommodationDetails: Record<string, any>,
  isMiniProgram: boolean,
  isUserLoggedIn: boolean,
  isBookFormEnabledForInactiveProperty: boolean,
  isWhitelabel: boolean,
  isDemoSubdomain: boolean,
  partner_uuid: string,
) => {
  const isPaymentEnabled =
    isPaymentActive(inv, isBookFormEnabledForInactiveProperty) &&
    !(await getLeadPaymentLeadSuccessStatus(lead_uuid)) &&
    !(await getPaymentInfoToRedirectPaymentPage(
      inv?.canonical_name,
      isPaymentActive(inv, isBookFormEnabledForInactiveProperty),
      inventory_id,
      dispatch,
      isUserLoggedIn,
    ));
  // Generate payment link
  if (isPaymentEnabled) {
    const params: any = {};
    if (lead_uuid) params.lead_uuid = lead_uuid;

    if (user_details?.name) params.name = user_details.name;

    if (user_details?.email) params.email = user_details.email;

    if (inventory_id) params.inventory_id = inventory_id;

    if (accommodationDetails.roomType) {
      params.config_name = getRoomName(inv, accommodationDetails.roomType);
    }

    if (accommodationDetails.duration) params.lease_duration = accommodationDetails.duration;

    if (accommodationDetails.custom_duration)
      params.custom_duration = accommodationDetails.custom_duration;

    if (accommodationDetails.arrival_date) params.arrival_date = accommodationDetails.arrival_date;

    // ── Mini Program: create payment & post message to native WeChatPay page ──
    // In the WeChat Mini Program we can't use Stripe. Instead, we create the
    // payment record here and hand off to the native wx.requestPayment() flow
    // via a postMessage that WebView.vue intercepts.
    if (isMiniProgram && typeof window !== "undefined" && (window as any).wx?.miniProgram) {
      try {
        const baseURL = window.location.origin;

        // Create payment record
        const paymentData = {
          name: user_details?.name || "",
          description: `Payment for ${
            getRoomName(inv, accommodationDetails.roomType) || inv?.name || ""
          }`,
          currency: "cny",
          gateway: "wechat",
          state: "pending",
          inventory_id,
          ...(lead_id ? { lead_id } : {}),
          user_details: {
            name: user_details?.name || "",
            email: user_details?.email || "",
            phone: user_details?.phone || "",
          },
          source_details: {
            user_agent: "miniProgram",
          },
        };

        const response = await axios.post(
          `${config.API_URL}/api/v0/payments`,
          { payment: paymentData },
          { headers: getUniAppHeaders() },
        );

        // Validate the response explicitly before trusting it. axios rejects
        // non-2xx by default (→ caught below), but a 2xx can still carry an
        // unexpected body.
        const isOkStatus = response?.status >= 200 && response?.status < 300;
        const payment = response?.data?.data;
        const hasValidToken = typeof payment?.token === "string" && payment.token.length > 0;

        if (!isOkStatus || !hasValidToken) {
          throw new Error(
            `Mini program payment creation returned an unexpected response (status: ${response?.status})`,
          );
        }

        // Build URLs directly from the confirmed token (no placeholder
        // substitution) so a missing/falsy token can never leak a malformed
        // URL to the native WeChat Pay page.
        const finalSuccessUrl = `${baseURL}/payments/complete?token=${payment.token}&nationality=${
          nationality || ""
        }`;
        const finalFailureUrl = `${baseURL}/payments/${payment.token}?payment_state=failed`;

        const wechatPayParams = [
          `token=${encodeURIComponent(payment.token)}`,
          `amount=${encodeURIComponent(String(payment.amount))}`,
          `currency=${encodeURIComponent((payment.currency || "cny").toUpperCase())}`,
          `description=${encodeURIComponent(paymentData.description)}`,
          `success_url=${encodeURIComponent(finalSuccessUrl)}`,
          `failure_url=${encodeURIComponent(finalFailureUrl)}`,
          ...buildWeChatPayBookingParams(inv, accommodationDetails),
        ].join("&");

        (window as any).wx.miniProgram.navigateTo({
          url: `/pages/WeChatPay/WeChatPay?${wechatPayParams}`,
        });

        updateBookingCompletedData({
          dispatch,
          inv,
          id: inventory_id,
          label: HAPPY_FLOW_TENANCY_CTAS.CONTINUE_PAYMENT,
          paymentDispatch: true,
        });
        disableLoading();
        return; // Don't push to history — mini program takes over
      } catch (err) {
        logger.error("[BookForm] Mini program payment creation failed:", err);
        // Do NOT fall through to the normal Stripe/H5 payment flow — that page
        // cannot run inside the WeChat mini program and would break the session.
        // Stop here.
        disableLoading();
        return;
      }
    }

    let paymentLink = getPaymentFullUrl(inv, params);
    if (["enquire_property", "book_tenancy"].includes(queryParams?.bookform_source)) {
      const paymentSource: any = {
        enquire_property: "clicked_book_enquire_now_property",
        book_tenancy: "clicked_book_tenancy_room",
      };
      paymentLink += `&payment_source=${paymentSource[queryParams.bookform_source]}`;
    } else if (queryParams?.bookform_source) {
      paymentLink += `&bookform_source=${queryParams.bookform_source}`;
    }

    if (nationality) {
      paymentLink = `${paymentLink}&nationality=${nationality}`;
    }

    if (isWhitelabel) {
      paymentLink = convertToWhitelabelURL(paymentLink, isDemoSubdomain, partner_uuid);
    }

    updateBookingCompletedData({
      dispatch,
      inv,
      id: inventory_id,
      label: HAPPY_FLOW_TENANCY_CTAS.CONTINUE_PAYMENT,
      paymentDispatch: true,
    });
    // added small delay - giving breathing space to fire all the events
    // await sleep(1000);
    disableLoading();
    // Set flag to prevent timer from starting on unmount
    if (isApp()) {
      cancelIdleTimer(IdleTimerStage.BOOK_FORM);
      localStorage.setItem("BOOK_FORM_SUBMITTED", "true");
    }
    history.push(paymentLink);
  } else {
    updateBookingCompletedData({
      dispatch,
      inv,
      id: inventory_id,
      label: HAPPY_FLOW_TENANCY_CTAS.VIEW_BOOKINGS,
    });
    disableLoading();
    // Set flag to prevent timer from starting on unmount
    if (isApp()) {
      cancelIdleTimer(IdleTimerStage.BOOK_FORM);
      localStorage.setItem("BOOK_FORM_SUBMITTED", "true");
    }
    if (isDesktop) history.push(formSubmitRedirectUrl);
    else history.push(formSubmitRedirectUrl);
  }
};

export const bookFormSubmitHandler = async ({
  data,
  countryName,
  skipGuarantorDetails,
  inv,
  eventsData,
  dispatch,
  history,
  enableLoading,
  disableLoading,
  isDesktop,
  isPaymentBeforeBookform = false,
  userDataForEvents,
  isMiniProgram = false,
  isUserLoggedIn = false,
  isWhitelabel = false,
  isDemoSubdomain = false,
  partner_uuid = "",
  is_application_form_enabled,
  isBookFormEnabledForInactiveProperty = false,
}: any) => {
  const { accommodationDetails, personalDetails, emergencyDetails, additionalDetails } = data;

  let formData = {
    ...accommodationDetails,
    ...personalDetails,
    ...emergencyDetails,
    ...additionalDetails,
  };

  const userDataForGtagEvent = {
    email: formData["user_details/email"],
    phone: formData["user_details/phone"],
    country_code: formData["user_details/phone_code"],
  };

  // merge phone code and phone number into one phone field
  if (formData["user_details/phone"])
    formData["user_details/phone"] = getPhoneWithCode(
      formData["user_details/phone_code"],
      formData["user_details/phone"],
    );

  if (formData["data/emergency_details/phone"])
    formData["data/emergency_details/phone"] = getPhoneWithCode(
      formData["data/emergency_details/phone_code"],
      formData["data/emergency_details/phone"],
    );

  // Modify dates to simple date string format
  if (formData["data/dob"])
    formData["data/dob"] = formatTextInputDateToString(formData["data/dob"]);
  if (formData["data/emergency_details/dob"])
    formData["data/emergency_details/dob"] = formatTextInputDateToString(
      formData["data/emergency_details/dob"],
    );

  if (formData["arrival_date"])
    formData["arrival_date"] = formatDateToString(formData["arrival_date"]);

  delete formData["user_details/phone_code"];
  delete formData["data/emergency_details/phone_code"];

  if (!skipGuarantorDetails) {
    formData = { ...formData, ...data["guarantorDetails"] };
    if (formData["data/guarantor_details/dob"])
      formData["data/guarantor_details/dob"] = formatTextInputDateToString(
        formData["data/guarantor_details/dob"],
      );

    if (formData["data/guarantor_details/phone"]) {
      formData["data/guarantor_details/phone"] = getPhoneWithCode(
        formData["data/guarantor_details/phone_code"],
        formData["data/guarantor_details/phone"],
      );

      delete formData["data/guarantor_details/phone_code"];
    }
  }

  const queryParams = getQueryParams();

  if (queryParams?.url) {
    formData["url"] = queryParams.url;
  }

  // const emailId = formData["user_details/email"];

  const getRoommateDetails = (roommateData: any) => {
    const formattedRoommateDetails: any = {};
    formattedRoommateDetails["data/roommates"] = roommateData?.alreadyHaveRoommate
      ? "true"
      : "false";
    if (formattedRoommateDetails["data/roommates"] === "true" && roommateData?.roommateEntries) {
      formattedRoommateDetails["data/no_of_roommates"] = roommateData?.roommateEntries?.length;
      formattedRoommateDetails["data/roommate_details"] = roommateData?.roommateEntries?.map(
        (roommate: any) => ({
          title: roommate?.title,
          name: roommate?.name,
          phone: roommate?.phone?.includes("+")
            ? roommate?.phone
            : `+${roommate?.phone_code.split("+")[1]}${roommate?.phone}`,
          email: roommate?.email,
        }),
      );
    }
    return formattedRoommateDetails;
  };

  if (data.roommateDetails) {
    formData = { ...formData, ...getRoommateDetails(data.roommateDetails) };
  }

  const appSourceDetails = getAppSourceDetails();
  const uniAppVersion = getCookie("is_uni_app") === "true" ? getCookie("x_app_version") : "";
  formData = {
    ...formData,
    meta: {
      ...(formData?.meta ? formData.meta : {}),
      payment_before_bookform: Boolean(isPaymentBeforeBookform),
    },
    source_details: {
      ...getFormattedSourceDetails(isWhitelabel, partner_uuid),
      ...appSourceDetails,
      ...(uniAppVersion ? { app_version: uniAppVersion } : {}),
    },
  };

  enableLoading(); // enabled loading for final step submit button

  const res = await submitBookForm(formData);
  if (res.message === "Form-created" && res.data) {
    const {
      inventory_id: inventoryId,
      uuid,
      lead_id: leadId,
      data_formatted: { lead_uuid },
    } = res.data;
    const leadSavedData: any = getParsedLocalStorageData("lead-form-one-success-data");
    const leadIdForPayment = leadId || leadSavedData?.id;
    const encryptedLeadUUID =
      encryptDataString(lead_uuid, "lead_user_lead_uuid").split("=")[1] || "";
    const { selectedRoom, selectedTenancy }: any = getSelectedRoomAndTenancy({
      rooms: inv.active_children,
      id: inventoryId,
    });
    const PriceForSelectedTenancy = getPriceForSelectedTenancy(inv, inventoryId);

    const currentInvObject = selectedTenancy || selectedRoom;

    const commissionCurrency =
      currentInvObject?.pricing?.currency &&
      currencyData?.find((currency) => currency.short_name === currentInvObject?.pricing?.currency)
        ?.standard_name;

    const commissionValue = { amount: null, currency: commissionCurrency };

    // @ts-ignore
    if (typeof gtag_report_conversion_3 == "function") {
      if (commissionValue?.currency) {
        // @ts-ignore
        gtag_report_conversion_3(undefined, commissionValue.currency, commissionValue.amount);
      }
      // @ts-ignore
      else if (typeof Sentry !== "undefined") {
        // @ts-ignore
        Sentry.captureException(
          new Error(`error in getting currency in inventory id ${inventoryId}`),
        );
      }
    }

    const roomDetailsProps = getRoomDetailsEventProps(getSectionNameFromUrl());
    gtagEventEmitter({
      action: "submit",
      category: "book_form",
      value: 1,
      label: countryName,
      ...eventsData,
      ...userDataForGtagEvent,
      ...roomDetailsProps,
    });
    logAppEvent({
      eventName: APP_EVENTS.BOOKFORM_SUBMITTED,
      payload: {
        property_id: inv?.id,
        room_name: selectedRoom?.name || "",
        tenancy_name: selectedTenancy?.name || "",
        user_name: formData["user_details/name"],
        user_email: formData["user_details/email"],
        user_phone: formData["user_details/phone"],
        form_id: res?.data?.id,
        move_in_date: formData["arrival_date"] || formData["moveInDate"],
        stay_duration: formData["custom_duration"] || formData["duration"],
        payment_types: inv?.payment_types?.join(",") || "",
        currency: inv?.pricing?.currency,
        price: inv?.pricing?.available_price || inv?.pricing?.price || inv?.pricing?.min_price,
        ...roomDetailsProps,
      },
    });

    gtagDuplicateEventEmitter({
      action: "purchase",
      value: `${PriceForSelectedTenancy}`,
      items: [
        {
          id: inventoryId,
          google_business_vertical: "hotel_rental",
        },
      ],
    });

    // @ts-ignore
    if (typeof snaptr !== "undefined" && snaptr !== null) {
      // reinitialize snapchat to set book-from email
      // @ts-ignore
      snaptr("init", config.snapchat_event_id, {
        user_email: userDataForEvents?.data?.email,
      });
      // @ts-ignore
      snaptr("track", "cart_checkout");
    }

    // TikTok pixel Book Form event
    if (typeof window !== "undefined" && window.ttq) {
      window.ttq.track("InitiateCheckout");
    }

    // Reddit Pixel Book Form Event
    if (typeof window !== "undefined" && window.rdt) {
      window.rdt("track", "Custom", {
        customEventName: "BookForm",
      });
    }
    // OpenAI Pixel Book Form Event
    // Fired as the standard event `registration_completed` (labelled "Book Form Completed" in the
    // OpenAI Ads dashboard) so it can be used as a conversion/optimization event in campaigns.
    // Standard event names are a fixed list defined by OpenAI — a custom event name cannot be
    // selected under a campaign, which is why this is not fired via `custom`.
    if (typeof window !== "undefined" && window.oaiq) {
      window.oaiq("init", { pixelId: "QL7N4mYF2wDxgg2RtFsUk7" });
      window.oaiq("measure", "registration_completed", { type: "customer_action" });
    }

    if (config?.IS_CHINA_APP && typeof window !== "undefined" && "_agl" in window) {
      // @ts-ignore
      window._agl.push(["track", ["success", { t: 18 }]]);
    }

    // checking query params
    const originUrl = document.location.origin;
    let formSubmitRedirectUrl = `/forms/submit?lead_uuid=${encryptedLeadUUID}&inventory_id=${inv?.id}&form_uuid=${uuid}&form_page_url=${originUrl}`;
    if (queryParams.bookform_source) {
      formSubmitRedirectUrl += `&bookform_source=${queryParams.bookform_source}`;
    }
    if (inv?.region?.canonical_name) {
      formSubmitRedirectUrl += `&region_canonical_name=${inv.region.canonical_name}`;
    }
    if (queryParams.payment_before_bookform)
      formSubmitRedirectUrl += `&payment_before_bookform=${queryParams.payment_before_bookform}`;
    if (isWhitelabel) {
      formSubmitRedirectUrl = convertToWhitelabelURL(
        formSubmitRedirectUrl,
        isDemoSubdomain,
        partner_uuid,
      );
    }

    handleEvents(
      uuid,
      {
        ...eventsData,
        ...userDataForGtagEvent,
        is_application_form_enabled,
      },
      inv,
    );

    // redirect to payment page for whitelabel
    if (encryptedLeadUUID && !isPaymentBeforeBookform) {
      // [P1-3171] https://amberhq.atlassian.net/browse/P1-3171
      // Redirect to payment form for payment enabled property after book-form submission
      redirectToPaymentPage(
        encryptedLeadUUID,
        leadIdForPayment,
        inv,
        res.data.user_details,
        formSubmitRedirectUrl,
        queryParams,
        res.data.data["data/nationality"],
        disableLoading,
        history,
        isDesktop,
        dispatch,
        formData.inventory_id,
        accommodationDetails,
        isMiniProgram,
        isUserLoggedIn,
        isBookFormEnabledForInactiveProperty,
        isWhitelabel,
        isDemoSubdomain,
        partner_uuid,
      );
    } else {
      updateBookingCompletedData({
        dispatch,
        inv,
        id: formData.inventory_id,
        label: HAPPY_FLOW_TENANCY_CTAS.VIEW_BOOKINGS,
      });
      disableLoading();
      if (isDesktop) history.push(formSubmitRedirectUrl);
      else history.push(formSubmitRedirectUrl); // safe check -> if lead_uuid is not present
    }
  } else {
    disableLoading();

    gtagEventEmitter({
      action: "submit",
      category: "book_form",
      value: 0,
      label: countryName,
      ...eventsData,
    }); // value accepts numeric value, thus 0 signifies failure

    // captureCustomMessage(`book_form >> ${emailId} >> failed`, {
    //   message: `failed with reason ${res.error?.message}`,
    //   response: error,
    //   formData,
    // });

    // if (callBack) callBack();

    const message = res.error?.message;
    const soldoutRoomErrorMessage = res?.error?.response?.data?.message;
    const defaultErrorText = soldoutRoomErrorMessage || "Something went wrong, please contact us";
    const serverErrorPhoneInvalid = "Error phone-not-valid";
    const isPhoneInvalidError = message === serverErrorPhoneInvalid;
    const displayMessage = isPhoneInvalidError ? "Invalid phone number" : defaultErrorText;
    const isNetworkError = message === "Network Error" || res.error?.name === "AbortError";

    // open the second step in case of phone num error
    // if (message === serverErrorPhoneInvalid && moveToStep) {
    //   moveToStep(2);
    // }

    if (
      !isPhoneInvalidError &&
      !isNetworkError &&
      !soldoutRoomErrorMessage &&
      // @ts-ignore
      typeof Sentry !== "undefined"
    ) {
      // push error to sentry
      // @ts-ignore
      Sentry.captureException(new Error("book form submission error"), {
        extra: { request: JSON.stringify(formData), response: res.error },
      });
    }
    const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
    toastNotificationDispatcher.openToast({
      title: "Error while submitting!",
      message: displayMessage,
    });
  }
};

const validateDobParts = (day: number, month: number, year: number) => {
  const errorMessage = "Invalid Date";
  const currentYear = new Date().getFullYear();
  const YEAR_LIMIT_END = 80;
  const YEAR_LIMIT_START = 14;

  const isValidDate = day >= 1 && day <= 31;
  const isValidMonth = month >= 1 && month <= 12;
  const isValidYear =
    year >= currentYear - YEAR_LIMIT_END && year <= currentYear - YEAR_LIMIT_START;

  if (!isValidDate || !isValidMonth || !isValidYear) return errorMessage;

  const dateObject = new Date(year, month - 1, day);
  if (
    dateObject.getDate() !== day ||
    dateObject.getMonth() + 1 !== month ||
    dateObject.getFullYear() !== year
  ) {
    return errorMessage;
  }

  return true;
};

const validateDate = (value: any) => {
  const errorMessage = "Invalid Date";

  if (typeof value === "string" && config.IS_CHINA_APP) {
    const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      return validateDobParts(Number(ymdMatch[3]), Number(ymdMatch[2]), Number(ymdMatch[1]));
    }
  }

  const dateParts =
    typeof value === "string" ? value?.split("-").map(Number) : Object.values(value)?.map(Number);

  if (dateParts.length !== 3) return errorMessage;

  const [day, month, year]: number[] = dateParts;

  return validateDobParts(day, month, year);
};

const formatDateString = (date: any) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSelectedDateGreaterThanToday = (date: any) => {
  if (date) {
    const selectedDate = new Date(date);
    const todayDate = new Date();

    const formattedArrivalDate = formatDateString(selectedDate);
    const formattedTodayDate = formatDateString(todayDate);

    // Compare the formatted date strings
    return formattedArrivalDate > formattedTodayDate;
  }
  return false;
};

const handleBookLeadSubmit = async ({
  data,
  inventory,
  dispatch,
  bookFormCompleted = [],
  paymentInitiated = [],
  bookFormInitiated = [],
  isUserLoggedIn,
  fromPaymentForm = false,
  isPaymentBeforeBookform = false,
  paymentInfo = {},
  history,
  savedAccommodationDetails,
  isWhitelabel = false,
  partner_uuid = "",
}: any) => {
  const queryParams = getQueryParams();
  let selectedRoomHavingImage = inventory;
  const { selectedRoom, selectedTenancy } = getSelectedRoomAndTenancy({
    rooms: inventory.active_children,
    id: queryParams?.inventory_id || savedAccommodationDetails?.inventory_id,
  });
  const selectedInventory = selectedTenancy || selectedRoom;
  if (selectedRoom?.images?.length > 0) {
    selectedRoomHavingImage = selectedRoom;
  }

  const options = await onSubmitLeadFormOne({
    data: {
      fullName: data["user_details/name"],
      email: data["user_details/email"],
      code: data["user_details/phone_code"],
      mobileNumber: data["user_details/phone"],
    },
    inventory: selectedInventory || inventory,
    isLeadFormFilled: false,
    inModal: false,
    redirectCB: () => console.log(""),
    inventoryRegion: null,
    openingSource: null,
    isHappyFlow: false,
    isOptIn: false,
    history: null,
    shouldReturnToBookFrom: true,
    isPaymentBeforeBookform,
    isWhitelabel,
    partner_uuid,
  });

  const response = await createLead(options);

  if (response.data) {
    localStorage.setItem("lead-form-one-success-data", JSON.stringify(response.data));
    publishWebState();

    // events
    const email_id = data["user_details/email"];
    const phone = data["user_details/phone"];
    const country_code = data["user_details/phone_code"];

    await fireLeadFormOneSuccessEvents({
      response,
      email_id,
      phone,
      country_code,
      flag: false,
      location: inventory?.location?.locality?.long_name,
      propertyName: inventory?.name,
      inventory,
      openingSource: "book_now",
      isPartner: false,
    });

    // do initiate api call and add reducer state update
    const isCookiePresent = getCookie("user_flow_token");

    if (!fromPaymentForm && !__DESKTOP__ && (isCookiePresent || isUserLoggedIn)) {
      if (selectedInventory && selectedInventory.id)
        initiateBookformAPI(selectedInventory.id, "contact_details_card");
    }

    await fetchPaymentInfoDetails({
      paymentInfo,
      fromPaymentForm,
      isUserLoggedIn,
      inventory,
      history,
      dispatch,
      savedAccommodationDetails,
    });

    dispatch(setLocalStorageData());
    const isBookFormCompleted =
      selectedInventory && bookFormCompleted.includes(selectedInventory.id);
    const isBookformInitiated =
      selectedInventory && bookFormInitiated.includes(selectedInventory.id);
    const isPaymentInitiated = selectedInventory && paymentInitiated.includes(selectedInventory.id);

    try {
      if (queryParams.inventory_id && selectedInventory) {
        if (fromPaymentForm && !isPaymentInitiated) {
          updateInitiatePayment({
            dispatch,
            inventory,
            selectedInventory,
            selectedRoom,
            selectedTenancy,
          });
        } else if (!fromPaymentForm && !isBookFormCompleted && !isBookformInitiated) {
          updateInitiateBookform({
            dispatch,
            selectedInventory,
            inventory,
            isTenancy: Boolean(selectedTenancy),
            selectedRoomHavingImage,
          });
        }
      } else {
        dispatch(updateEnquire({ inventoryId: inventory.id }));
        dispatch(updatePropertyData({ inventoryId: inventory.id }));
      }
    } catch (error) {
      if (typeof window !== undefined && typeof window.Sentry !== "undefined") {
        window.Sentry.captureException(`Bookform Personal Details Dispatcher Error -> ${error}`);
      }
    }
  }

  if (response.error) {
    console.error(response);
    // Google Analytics
    gtagEventEmitter({ action: "failure", category: "lead" });
    gtagDuplicateEventEmitter({ action: "failure_newleadform", category: "lead" });

    if (typeof window !== undefined && typeof window.Sentry !== "undefined") {
      window.Sentry.captureException(
        new Error(`Book form contact form Error -> ${response.error}`),
        {
          extra: { request: JSON.stringify(options), response: response.body },
        },
      );
    }
  }
};

export const updateBookingCompletedData = ({
  dispatch,
  inv,
  id,
  label,
  paymentDispatch = false,
}: any) => {
  const { selectedRoom, selectedTenancy } = getSelectedRoomAndTenancy({
    rooms: inv.active_children,
    id,
  });
  try {
    if (selectedRoom || selectedTenancy) {
      // updating userHistory reducer inventoryData -> bookform_completed state
      const book_form_completed = getActionInitiatedData({
        inventory: selectedTenancy || selectedRoom,
        label,
        isTenancy: Boolean(selectedTenancy),
      });

      const link = getPaymentFormLink({
        rooms: inv.active_children,
        canonicalName: inv.canonical_name,
        id: selectedTenancy?.id || selectedRoom?.id,
      });

      dispatch(updatePropertyData({ inventoryId: inv.id, book_form_completed }));

      if (selectedTenancy) {
        dispatch(
          updatePropertyTenancy({
            roomId: selectedRoom.id,
            tenancyId: selectedTenancy.id,
            label,
            book_form_completed: true,
          }),
        );
      } else {
        dispatch(
          updatePropertyRoom({
            roomId: selectedRoom.id,
            label,
            book_form_completed: true,
          }),
        );
      }
      dispatch(
        updateBookFormCompleted({
          inventoryId: inv.id,
          id: selectedTenancy?.id || selectedRoom?.id,
          updatedBookFormCompletedData: {
            link: paymentDispatch ? link : "/profile/leads",
            label: paymentDispatch
              ? HAPPY_FLOW_TENANCY_CTAS.CONTINUE_PAYMENT
              : HAPPY_FLOW_TENANCY_CTAS.VIEW_BOOKINGS,
          },
        }),
      );

      if (paymentDispatch) {
        updateInitiatePayment({
          dispatch,
          inventory: inv,
          selectedInventory: selectedRoom || selectedTenancy,
          selectedRoom,
          selectedTenancy,
        });
      }
    }
  } catch (error) {
    if (typeof window !== undefined && typeof window.Sentry !== "undefined") {
      window.Sentry.captureException(`Bookform Dispatcher Error -> ${error}`);
    }
  }
};

export const updateInitiateBookform = ({
  dispatch,
  selectedInventory,
  inventory,
  isTenancy,
  selectedRoomHavingImage,
}: any) => {
  const bookFormInitiated = getActionInitiatedData({
    inventory: selectedInventory,
    label: HAPPY_FLOW_TENANCY_CTAS.CONTINUE_BOOKING,
    isTenancy,
  });
  const link = getBookFormLink({
    rooms: inventory.active_children,
    canonicalName: inventory.canonical_name,
    id: selectedInventory.id,
  });

  dispatch(updatePropertyData({ inventoryId: inventory.id, bookFormInitiated }));

  dispatch(
    updateBookFormInitiated({
      inventoryId: inventory.id,
      id: selectedInventory.id,
      updatedBookFormInitiatedData: {
        link,
        label: HAPPY_FLOW_TENANCY_CTAS.CONTINUE_BOOKING,
        name: inventory.name,
        pricing: selectedInventory.pricing,
        config_images: selectedRoomHavingImage.images,
      },
    }),
  );
  if (isTenancy) {
    dispatch(
      updatePropertyTenancy({
        inventoryId: inventory.id,
        roomId: selectedInventory.parent_id,
        tenancyId: selectedInventory.id,
        label: HAPPY_FLOW_TENANCY_CTAS.CONTINUE_BOOKING,
        roomLabel: HAPPY_FLOW_TENANCY_CTAS.CONTACT_US,
        book_form_initiated: true,
        ...(bookFormInitiated ? { bookFormInitiated } : {}),
      }),
    );
  } else {
    dispatch(
      updatePropertyRoom({
        inventoryId: inventory.id,
        roomId: selectedInventory.id,
        label: HAPPY_FLOW_TENANCY_CTAS.CONTINUE_BOOKING,
        book_form_initiated: true,
        ...(bookFormInitiated ? { bookFormInitiated } : {}),
      }),
    );
  }
};

const calculateSpan = (isMobile: any, inventoryForm: any) => {
  const showTitle = !inventoryForm || inventoryForm?.title?.show;
  if (isMobile) {
    return showTitle ? 8 : 12;
  }
  return showTitle ? 4 : 6;
};

const findNextPending = ({
  completedStepMap,
  isApplicationForm = false,
  isDocSectionEnabled = false,
}: any) => {
  const activeIndex = completedStepMap.indexOf("active");

  if (activeIndex === -1) {
    return -1;
  }

  const nextPendingIndex = completedStepMap
    .slice(activeIndex + 1)
    .findIndex((status: any) => status === "pending");

  const finalNextPendingIndex = nextPendingIndex === -1 ? -1 : activeIndex + 1 + nextPendingIndex;
  if (
    isApplicationForm &&
    finalNextPendingIndex === completedStepMap.length - 1 &&
    isDocSectionEnabled
  )
    return finalNextPendingIndex - 1;

  return finalNextPendingIndex;
};

const getSavedValues = (keys: any, data: any) =>
  keys.reduce((obj: any, key: any) => {
    if (data[key] !== undefined) {
      obj[key] = data[key];
    }
    return obj;
  }, {});

const isAllPersonalDetailsPresent = (personalDetails: any) =>
  Boolean(
    personalDetails["user_details/name"] &&
      personalDetails["user_details/email"] &&
      personalDetails["user_details/phone"] &&
      personalDetails["user_details/phone_code"],
  );

const fetchPaymentInfoDetails = async ({
  paymentInfo,
  fromPaymentForm,
  isUserLoggedIn,
  inventory,
  history,
  savedAccommodationDetails,
  dispatch,
}: any) => {
  const isCookiePresent = getCookie("user_flow_token");
  const queryParams = getQueryParams();
  const leadSavedData: any = getParsedLocalStorageData("lead-form-one-success-data");
  const encryptedLeadUUID =
    encryptDataString(leadSavedData?.uuid, "lead_user_lead_uuid").split("=")[1] || "";
  const { selectedRoom, selectedTenancy } = getSelectedRoomAndTenancy({
    rooms: inventory.active_children,
    id: queryParams?.inventory_id || savedAccommodationDetails?.inventory_id,
  });
  const selectedInventory = selectedTenancy || selectedRoom;

  const { payment_info = paymentInfo?.payment_info || [] } =
    fromPaymentForm &&
    (isCookiePresent || isUserLoggedIn) &&
    selectedInventory &&
    paymentInfo?.id != selectedInventory?.id
      ? await fetchPaymentInfo({
          id: inventory?.canonical_name,
          inventory_id: selectedInventory.id,
          source: "contact details payment form",
          nationality: queryParams["nationality"] ?? false,
        })
      : {};

  if (payment_info) {
    dispatch(updatePaymentInfo({ payment_info }));
    dispatch(
      updatePriceSummary({
        ...getPaymentSummary(payment_info),
        payment_info,
      }),
    );
  }

  if (
    fromPaymentForm &&
    history &&
    selectedInventory &&
    payment_info.length > 0 &&
    getTotalPaymentValue(payment_info)?.amount === 0
  ) {
    // checking query params
    const originUrl = document.location.origin;
    let formSubmitRedirectUrl = `/forms/submit?lead_uuid=${encryptedLeadUUID}&inventory_id=${selectedInventory.id}&form_page_url=${originUrl}`;
    if (queryParams.bookform_source) {
      formSubmitRedirectUrl += `&bookform_source=${queryParams.bookform_source}`;
    }
    if (inventory?.region?.canonical_name) {
      formSubmitRedirectUrl += `&region_canonical_name=${inventory.region.canonical_name}`;
    }
    if (queryParams.payment_before_bookform)
      formSubmitRedirectUrl += `&payment_before_bookform=${queryParams.payment_before_bookform}`;

    history.push(formSubmitRedirectUrl);
  }
};

const formatRoommateNames = (
  data: Array<{
    title: string;
    name: string;
    phone_code: string;
    phone: string;
    email: string;
  }>,
): Record<string, string> => {
  const result: Record<string, string> = {};
  const limit = 3;

  const keys = ["roommateOne", "roommateTwo", "roommateThree"];

  data.slice(0, limit).forEach((item, index) => {
    if (index === 2 && data.length > limit) {
      const remaining = data.length - limit;
      result[keys[index]] = `${item.name}+${remaining}`;
    } else {
      result[keys[index]] = item.name;
    }
  });

  return result;
};

const validateNumpad = (value = "") => {
  if (/[,\-.\s]/.test(value)) {
    return "Invalid";
  }
  return false;
};

export {
  getAccommodationFormDefaultValue,
  validateDate,
  calculateSpan,
  isSelectedDateGreaterThanToday,
  handleBookLeadSubmit,
  findNextPending,
  getSavedValues,
  isAllPersonalDetailsPresent,
  fetchPaymentInfoDetails,
  getPhoneWithCode,
  formatTextInputDateToString,
  formatDateToString,
  formatRoommateNames,
  checkIfTenancyAvailable,
  validateNumpad,
};

export const translateTextObjectToChinese = (object: any, t: any) =>
  Object.keys(object).reduce((acc: Record<string, string>, key) => {
    acc[key] = t(object[key]);
    return acc;
  }, {});

export const BOOK_FORM_CHINA_DOB_PLACEHOLDER = "YYYY-MM-DD";
export const BOOK_FORM_CHINA_ARRIVAL_PLACEHOLDER = "YYYY年M月D日";

export const getBookFormDobPlaceholder = (fallback: string) =>
  config.IS_CHINA_APP ? BOOK_FORM_CHINA_DOB_PLACEHOLDER : fallback;

/** Picker selection → stored DOB string (DD-MM-YYYY on .com, YYYY-MM-DD on .cn). */
export const formatDobFromPickerSelection = (d: {
  dayState: string | number;
  monthState: string | number;
  yearState: string | number;
}) => {
  const day = String(d.dayState).padStart(2, "0");
  const month = String(d.monthState).padStart(2, "0");
  const year = String(d.yearState);
  if (config.IS_CHINA_APP) return `${year}-${month}-${day}`;
  return `${day}-${month}-${year}`;
};

/** Sync picker part state from a stored DOB display string. */
export const getDobPickerPartsFromDisplay = (displayDob: string) => {
  if (!displayDob) return null;
  if (config.IS_CHINA_APP) {
    const [year, month, day] = displayDob.split("-");
    if (year?.length === 4 && month && day) return { day, month, year };
  }
  const [day, month, year] = displayDob.split("-");
  if (day && month && year) return { day, month, year };
  return null;
};

/** API / stored DOB → in-field display (DD-MM-YYYY on .com, YYYY-MM-DD on .cn). */
export const toDisplayDob = (dob: string) => {
  if (!dob) return dob;
  const isoMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    if (config.IS_CHINA_APP) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  const dmyMatch = dob.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) {
    if (config.IS_CHINA_APP) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    return dob;
  }
  return dob;
};
